import { createWrapper, HYDRATE } from "next-redux-wrapper"
import { applyMiddleware, legacy_createStore as createStore, Middleware } from "redux"
import { createEpicMiddleware } from "redux-observable"
import * as uuid from "uuid"
import { createApiServices } from "../../api/createServices"
import { Config } from "../../utils/config"
import { setStore } from "../../utils/redux"
import { getIsSuperUser } from "../auth/selectors"
import configureEpics from "../rxjs/configureEpics"
import epics from "../rxjs/epics"
import { withoutClientHydration } from "./hydration"
import { mainReducer } from "./mainReducer"
import { StateObject } from "./types"
import { actionEmitMiddleware } from "./redux-bus"

const getMiddlewares = () => {
    let storeRef: any = null

    const rxjsDeps = {
        api: createApiServices(),
        idgen: {
            next: () => uuid.v4(),
        },
        config: Config,
        getState: () => storeRef.getState(),
    }

    const epicMiddleware = createEpicMiddleware({
        dependencies: rxjsDeps,
    })
    const m: Middleware[] = [actionEmitMiddleware, epicMiddleware]

    const { createLogger } = require("redux-logger")

    const logger = createLogger({
        predicate: getState =>
            // Log just in browser (client-side) and on DEV (or on PROD but just for super-user).
            typeof window !== "undefined" && (Config.environment !== "production" || getIsSuperUser(getState())),
        collapsed: () => true, // All actions collapsed
    })
    m.push(logger)

    return {
        middleware: applyMiddleware(...m),
        epicMiddleware: epicMiddleware,
        setStoreRef: (store: any) => {
            storeRef = store
        },
    }
}

const wrapperReducer = (state, action) => {
    if (action.type === HYDRATE) {
        const nextState = {
            ...state,
            // use previous state
            ...action.payload, // apply delta from hydration
        }
        return nextState
    } else {
        return mainReducer(state, action)
    }
}

export const initStore = () => {
    const { middleware, epicMiddleware, setStoreRef } = getMiddlewares()
    const store = createStore(wrapperReducer, middleware)
    setStoreRef(store)
    epicMiddleware.run(configureEpics(epics))
    setStore(store)
    return store
}

export const initStoreForTests = (initialState?: Partial<StateObject>) => {
    const { middleware, epicMiddleware, setStoreRef } = getMiddlewares()
    const store = createStore(wrapperReducer, initialState, middleware)
    setStoreRef(store)
    epicMiddleware.run(configureEpics(epics))
    return store
}

export const reduxWrapper = createWrapper(initStore)

export const getInitialAppProps: typeof reduxWrapper.getInitialAppProps = callback =>
    withoutClientHydration(reduxWrapper.getInitialAppProps(callback))
